import { useEffect, useState } from 'react';

import { CkEditorField } from '../../components/ui/CkEditorField';
import { ApiError } from '../../lib/api-client';
import { confirmAction, confirmUpdate } from '../../lib/confirm-service';
import { useToast } from '../../lib/toast';
import { useFinalizeIpdDischarge, useIpdDischargeSummary, useSaveIpdDischargeSummary } from './ipd-daycare.queries';
import { FieldLabel } from './ipd-daycare.shared';
import { formatIpdDateTime, toDateTimeInput } from './ipd-daycare.utils';
import type { IpdDaycareAdmission, IpdDischargeType, UpsertIpdDischargeSummaryPayload } from './types';

const dischargeTypes: IpdDischargeType[] = ['Normal', 'Referral', 'LAMA', 'DORB', 'DAMA', 'Death'];

const blankPayload = (): UpsertIpdDischargeSummaryPayload => ({
  dischargeDate: toDateTimeInput(new Date().toISOString()),
  dischargeType: 'Normal',
  referralHospitalName: null,
  nextAppointmentDate: null,
  finalDiagnosis: null,
  presentingComplaints: null,
  presentingIllnessSummary: null,
  physicalExaminationAtAdmission: null,
  investigationsSummary: null,
  courseInHospital: null,
  dischargeAdvice: null,
  remark: null,
});

export const IpdDischargeSummaryForm = ({ admission }: { admission: IpdDaycareAdmission }) => {
  const query = useIpdDischargeSummary(admission.id);
  const saveDraft = useSaveIpdDischargeSummary(admission.id);
  const finalize = useFinalizeIpdDischarge(admission.id);
  const { showToast } = useToast();
  const [form, setForm] = useState<UpsertIpdDischargeSummaryPayload>(blankPayload);

  useEffect(() => {
    if (!query.data) return;
    setForm({
      dischargeDate: toDateTimeInput(query.data.dischargeDate),
      dischargeType: query.data.dischargeType,
      referralHospitalName: query.data.referralHospitalName,
      nextAppointmentDate: query.data.nextAppointmentDate ? query.data.nextAppointmentDate.slice(0, 10) : null,
      finalDiagnosis: query.data.finalDiagnosis,
      presentingComplaints: query.data.presentingComplaints,
      presentingIllnessSummary: query.data.presentingIllnessSummary,
      physicalExaminationAtAdmission: query.data.physicalExaminationAtAdmission,
      investigationsSummary: query.data.investigationsSummary,
      courseInHospital: query.data.courseInHospital,
      dischargeAdvice: query.data.dischargeAdvice,
      remark: query.data.remark,
    });
  }, [query.data]);

  const set = <K extends keyof UpsertIpdDischargeSummaryPayload>(key: K, value: UpsertIpdDischargeSummaryPayload[K]) => setForm((current) => ({ ...current, [key]: value }));

  const finalized = query.data?.finalized ?? false;
  const alreadyDischarged = admission.status === 'discharged';

  const submitDraft = () => {
    void confirmUpdate('this discharge summary').then((confirmed) => {
      if (!confirmed) return;
      saveDraft.mutate(form, {
        onSuccess: () => showToast('Discharge summary saved successfully.', 'success'),
        onError: (error) => showToast(error instanceof ApiError ? error.message : 'The discharge summary could not be saved.', 'error'),
      });
    });
  };
  const submitFinalize = async () => {
    const confirmed = await confirmAction({
      title: 'Discharge Patient?',
      description: 'This will save the discharge summary, release the bed, and mark the admission as discharged.',
      confirmLabel: 'Yes, Discharge',
      tone: 'danger',
    });
    if (!confirmed) return;
    saveDraft.mutate(form, {
      onSuccess: () => finalize.mutate(undefined, {
        onSuccess: () => showToast('Patient discharged successfully.', 'success'),
        onError: (error) => showToast(error instanceof ApiError ? error.message : 'The discharge could not be finalized.', 'error'),
      }),
      onError: (error) => showToast(error instanceof ApiError ? error.message : 'The discharge summary could not be saved.', 'error'),
    });
  };

  return (
    <div className="opd-prescription">
      <header className="opd-bill-banner">
        <div className="opd-bill-banner-row"><span>Discharge Summary — {admission.patientSnapshot.name}</span></div>
        <p>
          IPD No {admission.ipdNumber}
          {finalized && query.data?.finalizedAt ? ` · Discharged ${formatIpdDateTime(query.data.finalizedAt)} by ${query.data.finalizedBy?.name ?? '-'}` : alreadyDischarged ? ' · Already discharged' : ' · Draft'}
        </p>
      </header>

      <section className="ipd-daycare-section">
        <div className="ipd-daycare-admission-row">
          <label><FieldLabel required>Discharge Date &amp; time</FieldLabel><input type="datetime-local" value={form.dischargeDate} disabled={finalized} onChange={(event) => set('dischargeDate', event.target.value)} /></label>
          <label><FieldLabel required>Discharge Type</FieldLabel><select value={form.dischargeType} disabled={finalized} onChange={(event) => set('dischargeType', event.target.value as IpdDischargeType)}>{dischargeTypes.map((type) => <option key={type}>{type}</option>)}</select></label>
          {form.dischargeType === 'Referral' ? (
            <label><FieldLabel>Referral Hospital Name</FieldLabel><input value={form.referralHospitalName ?? ''} disabled={finalized} onChange={(event) => set('referralHospitalName', event.target.value || null)} /></label>
          ) : null}
          <label><FieldLabel>Next Appointment Date</FieldLabel><input type="date" value={form.nextAppointmentDate ?? ''} disabled={finalized} onChange={(event) => set('nextAppointmentDate', event.target.value || null)} /></label>
          <label><CkEditorField label="Final Diagnosis at time of Discharge" value={form.finalDiagnosis} disabled={finalized} onChange={(next) => set('finalDiagnosis', next || null)} /></label>
          <label><CkEditorField label="Presenting Complaints with Duration and Reason for Admission" value={form.presentingComplaints} disabled={finalized} onChange={(next) => set('presentingComplaints', next || null)} /></label>
          <label><CkEditorField label="Summary of Presenting Illness" value={form.presentingIllnessSummary} disabled={finalized} onChange={(next) => set('presentingIllnessSummary', next || null)} /></label>
          <label><CkEditorField label="Key findings on physical examination at time of admission" value={form.physicalExaminationAtAdmission} disabled={finalized} onChange={(next) => set('physicalExaminationAtAdmission', next || null)} /></label>
          <label><CkEditorField label="Summary of key investigations during Hospitalization" value={form.investigationsSummary} disabled={finalized} onChange={(next) => set('investigationsSummary', next || null)} /></label>
          <label><CkEditorField label="Course in Hospital including complications" value={form.courseInHospital} disabled={finalized} onChange={(next) => set('courseInHospital', next || null)} /></label>
          <label><CkEditorField label="Advice on Discharge" value={form.dischargeAdvice} disabled={finalized} onChange={(next) => set('dischargeAdvice', next || null)} /></label>
          {form.dischargeType !== 'Normal' ? (
            <label><CkEditorField label="Remark" value={form.remark} disabled={finalized} onChange={(next) => set('remark', next || null)} /></label>
          ) : null}
        </div>
      </section>

      {finalized ? (
        <p className="opd-bill-empty-note">This discharge has been finalized and can no longer be edited.</p>
      ) : (
        <div className="opd-form-actions">
          <button className="button button-secondary" type="button" disabled={saveDraft.isPending} onClick={submitDraft}>{saveDraft.isPending ? 'Saving…' : 'Save In Draft'}</button>
          <button className="button button-primary" type="button" disabled={saveDraft.isPending || finalize.isPending} onClick={() => void submitFinalize()}>{finalize.isPending ? 'Discharging…' : 'Save & Discharge'}</button>
        </div>
      )}
    </div>
  );
};
